import { useState } from 'react';
import type { BoardSummary } from '../persist/idb';
import { Dialog } from './Dialog';

interface Props {
  readonly boards: readonly BoardSummary[];
  readonly currentId: string | null;
  readonly onOpen: (id: string) => void;
  readonly onCreate: () => void;
  readonly onDuplicate: (id: string) => void;
  readonly onDelete: (id: string) => void;
  readonly onDeleteSamples: () => void;
  readonly onImport: () => void;
  readonly onExportAll: () => void;
  readonly onClose: () => void;
}

export function BoardsDialog(props: Props) {
  const { boards, currentId, onOpen, onCreate, onDuplicate, onDelete, onDeleteSamples, onImport, onExportAll, onClose } =
    props;
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmSamples, setConfirmSamples] = useState(false);
  const sampleCount = boards.filter((b) => b.isSample).length;

  return (
    <Dialog
      title="Boards"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onImport}>
            Import JSON
          </button>
          <button type="button" className="btn" onClick={onExportAll} disabled={boards.length === 0}>
            Export all as backup
          </button>
          <button type="button" className="btn btn-primary" onClick={onCreate}>
            New board
          </button>
        </>
      }
    >
      {boards.length === 0 ? (
        <div className="prose">
          <p>No boards yet.</p>
          <p>Create one, or import a JSON file you exported earlier.</p>
        </div>
      ) : (
        <div className="board-list">
          {boards.map((board) => (
            <div key={board.id} className="board-item" aria-current={board.id === currentId}>
              <button
                type="button"
                className="open"
                onClick={() => onOpen(board.id)}
                aria-label={`Open ${board.name}`}
              >
                <span className="name">{board.name}</span>
                <span className="meta">
                  {board.elementCount} element{board.elementCount === 1 ? '' : 's'} · edited{' '}
                  {new Date(board.updatedAt).toLocaleString()}
                </span>
              </button>
              {board.isSample ? <span className="badge">Sample</span> : null}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => onDuplicate(board.id)}
                aria-label={`Duplicate ${board.name}`}
              >
                Duplicate
              </button>
              {confirmId === board.id ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      onDelete(board.id);
                      setConfirmId(null);
                    }}
                  >
                    Delete for good
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setConfirmId(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => setConfirmId(board.id)}
                  aria-label={`Delete ${board.name}`}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {sampleCount > 0 ? (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <p className="prose" style={{ marginBottom: 8 }}>
            {sampleCount} sample board{sampleCount === 1 ? '' : 's'} shipped with the app. They are ordinary
            boards and nothing depends on them.
          </p>
          {confirmSamples ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  onDeleteSamples();
                  setConfirmSamples(false);
                }}
              >
                Delete all sample boards
              </button>
              <button type="button" className="btn" onClick={() => setConfirmSamples(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-danger" onClick={() => setConfirmSamples(true)}>
              Delete sample boards
            </button>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
